import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaMapMarkerAlt, FaHome, FaMap, FaGlobe, FaEdit, FaPlus, FaSave, FaTimes, FaWindowClose } from 'react-icons/fa';
import mainService from '../services/mainService';
import './MyAddress.css';

const MyAddress = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [modalSuccess, setModalSuccess] = useState(false);
  const [formData, setFormData] = useState({
    Area: '',
    Location: '',
    Address: ''
  });

  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        setLoading(true);
        setError(null);

        const userDataStr = localStorage.getItem('userData');
        if (!userDataStr) {
          setError('Please login to view your addresses.');
          return;
        }

        const user = JSON.parse(userDataStr);
        const customerId = user.CustomerId || user.Id;
        if (!customerId) {
          setError('Customer information not found.');
          return;
        }

        const data = await mainService.GetCustomerAddresses(customerId);
        setAddresses(Array.isArray(data) ? data : [data]);
      } catch (err) {
        console.error('Failed to load addresses:', err);
        setError('Failed to load your address information. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchAddresses();
  }, []);

  const handleEditClick = (address, index) => {
    setEditingAddress({ ...address, index });
    setIsAdding(false);
    setFormData({
      Area: address.Area || '',
      Location: address.Location || '',
      Address: address.Address || ''
    });
    setShowModal(true);
    setModalError(null);
    setModalSuccess(false);
  };

  const handleAddClick = () => {
    setEditingAddress(null);
    setIsAdding(true);
    setFormData({
      Area: '',
      Location: '',
      Address: ''
    });
    setShowModal(true);
    setModalError(null);
    setModalSuccess(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAddress(null);
    setIsAdding(false);
    setModalError(null);
    setModalSuccess(false);
    setFormData({
      Area: '',
      Location: '',
      Address: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setModalLoading(true);
      setModalError(null);
      setModalSuccess(false);

      const userDataStr = localStorage.getItem('userData');
      if (!userDataStr) {
        setModalError('Please login to manage your addresses.');
        return;
      }

      const user = JSON.parse(userDataStr);
      const customerId = user.CustomerId || user.Id;
      if (!customerId) {
        setModalError('Customer information not found.');
        return;
      }

      // Validate required fields
      if (!formData.Area || !formData.Location || !formData.Address) {
        setModalError('Please fill in all required fields (Area, Location, Address).');
        return;
      }

             const submitData = {
         ...formData,
         CustomerId: customerId
       };

       console.log('Submitting address data:', submitData);

       let response;
       if (isAdding) {
         // Add new address
         response = await mainService.Addnewaddress(submitData);
       } else {
         // Edit existing address
         submitData.Id = editingAddress.Id;
         response = await mainService.EditAddress(submitData);
       }

      if (response) {
        setModalSuccess(true);
        // Refresh addresses
        const updatedAddresses = await mainService.GetCustomerAddresses(customerId);
        setAddresses(Array.isArray(updatedAddresses) ? updatedAddresses : [updatedAddresses]);
        
        // Close modal after 2 seconds
        setTimeout(() => {
          setShowModal(false);
          setModalSuccess(false);
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to save address:', err);
      setModalError('Failed to save address. Please try again later.');
    } finally {
      setModalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="myaddress-page">
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner" />
            <p>Loading your address information...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="myaddress-page">
        <div className="container">
          <div className="error-container">
            <p className="error-message">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="myaddress-page">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">My Address</h1>
        </div>

        <div className="address-content">
          <div className="address-actions">
            <button className="add-address-btn" onClick={handleAddClick}>
              <FaPlus />
              Add New Address
            </button>
          </div>

          {addresses.length === 0 ? (
            <div className="no-addresses">
              <div className="no-addresses-icon">
                <FaMapMarkerAlt />
              </div>
              <h3>No Addresses Found</h3>
              <p>You haven't added any addresses yet.</p>
            </div>
          ) : (
            <div className="addresses-grid">
              {addresses.map((address, index) => (
                <div key={index} className="address-card">
                                     <div className="address-header">
                     <div className="address-icon">
                       <FaHome />
                     </div>
                     <h3 className="address-title">Address {index + 1}</h3>
                     <button 
                       className="edit-address-btn" 
                       onClick={() => handleEditClick(address, index)}
                       title="Edit Address"
                     >
                       <FaEdit />
                     </button>
                   </div>

                  <div className="address-details">
                    {address.Area && (
                      <div className="address-item">
                        <div className="address-item-icon">
                          <FaMap />
                        </div>
                        <div className="address-item-content">
                          <label className="address-label">Area:</label>
                          <span className="address-value">{address.Area}</span>
                        </div>
                      </div>
                    )}

                    {address.Location && (
                      <div className="address-item">
                        <div className="address-item-icon">
                          <FaGlobe />
                        </div>
                        <div className="address-item-content">
                          <label className="address-label">Location:</label>
                          <span className="address-value">{address.Location}</span>
                        </div>
                      </div>
                    )}

                    {address.Address && (
                      <div className="address-item">
                        <div className="address-item-icon">
                          <FaMapMarkerAlt />
                        </div>
                        <div className="address-item-content">
                          <label className="address-label">Address:</label>
                          <span className="address-value">{address.Address}</span>
                        </div>
                      </div>
                    )}

                    {address.POBox && (
                      <div className="address-item">
                        <div className="address-item-icon">
                          <FaMapMarkerAlt />
                        </div>
                        <div className="address-item-content">
                          <label className="address-label">P.O. Box:</label>
                          <span className="address-value">{address.POBox}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
                         </div>
           )}
         </div>

         {/* Address Modal */}
         {showModal && (
           <div className="modal-overlay" onClick={handleCloseModal}>
             <div className="modal-content" onClick={(e) => e.stopPropagation()}>
               <div className="modal-header">
                 <h3 className="modal-title">
                   {isAdding ? 'Add New Address' : 'Edit Address'}
                 </h3>
                 <button className="modal-close-btn" onClick={handleCloseModal}>
                   <FaWindowClose />
                 </button>
               </div>
               
               <div className="modal-body">
                 {modalError && (
                   <div className="error-message modal-error">
                     {modalError}
                   </div>
                 )}
                 
                 {modalSuccess && (
                   <div className="success-message">
                     {isAdding ? 'Address added successfully!' : 'Address updated successfully!'}
                   </div>
                 )}

                 <form className="address-form" onSubmit={handleSubmit}>
                   <div className="form-group">
                     <label className="form-label">Area:</label>
                     <input
                       type="text"
                       name="Area"
                       value={formData.Area}
                       onChange={handleInputChange}
                       className="form-input"
                       placeholder="Enter area"
                       required
                     />
                   </div>

                   <div className="form-group">
                     <label className="form-label">Location:</label>
                     <input
                       type="text"
                       name="Location"
                       value={formData.Location}
                       onChange={handleInputChange}
                       className="form-input"
                       placeholder="Enter location"
                       required
                     />
                   </div>

                   <div className="form-group">
                     <label className="form-label">Address:</label>
                     <textarea
                       name="Address"
                       value={formData.Address}
                       onChange={handleInputChange}
                       className="form-input"
                       placeholder="Enter full address"
                       rows="3"
                       required
                     />
                   </div>

                   <div className="modal-actions">
                     <button 
                       type="submit" 
                       className="save-btn" 
                       disabled={modalLoading}
                     >
                       <FaSave />
                       {modalLoading ? 'Saving...' : (isAdding ? 'Add Address' : 'Save Changes')}
                     </button>
                     <button 
                       type="button" 
                       className="cancel-btn" 
                       onClick={handleCloseModal}
                       disabled={modalLoading}
                     >
                       <FaTimes />
                       Cancel
                     </button>
                   </div>
                 </form>
               </div>
             </div>
           </div>
         )}
       </div>
     </div>
   );
 };

export default MyAddress;
